/* eslint-disable fp/no-mutating-methods */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { groupBy } from 'ramda'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { dataGridSx } from 'src/v3/manage/styles'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectRoles,
  selectUseEvents,
  selectUser,
} from 'src/v3/settings/settings.slice'
import AddIcon from '@mui/icons-material/Add'
import {
  AddUserIcon,
  CalendarIcon,
  ClockIcon,
  CloseIcon,
  ConfirmIcon,
  GroupIcon,
  LocationIcon,
  SearchIcon,
} from 'src/v3/core/icons'
import { accreditationDict } from 'src/v3/events/accreditations'
import axios, { pluckError } from 'src/utilities/http-client'
import { Event, EventSession, EventTypeName } from '../core/types'
import { DeleteIcon, EditIcon, UserIcon } from '../core/icons'
import {
  accreditationFilterChanged,
  activeEventChanged,
  dateLowerFilterChanged,
  dateUpperFilterChanged,
  eventTypeFilterChanged,
  fetchAccreditations,
  fetchEventAccreditations,
  fetchEvents,
  fetchInstructors,
  fetchInvites,
  fetchSessions,
  fetchUsers,
  foamfratFilterChanged,
  selectAccreditationFilter,
  selectAccreditationsByType,
  selectAccreditationsStatus,
  selectActiveEvent,
  selectDateLowerFilter,
  selectDateUpperFilter,
  selectEventAccreditations,
  selectEventAccreditationsStatus,
  selectEvents,
  selectEventsStatus,
  selectEventTypeFilter,
  selectFoamfratFilter,
  selectInstructorsStatus,
  selectInvites,
  selectInvitesStatus,
  selectInstructors,
  selectSessions,
  selectSessionsStatus,
  selectUsersByLinkedService,
  selectUsersStatusByLinkedService,
} from './events.slice'
import InviteUsers from './invite-users'
import { useAutoFetch } from '../hooks/use-auto-fetch'

dayjs.extend(utc)
dayjs.extend(timezone)

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let [nameFilter, setNameFilter] = useState<string>('')
  // let [viewMode, setViewMode] = useState<string>('List')
  let [usersSession, setUsersSession] = useState<string>()
  let [inviteSession, setInviteSession] = useState<string>()
  let [checkedUsers, setCheckedUsers] = useState<string[]>([])
  let [inviting, setInviting] = useState<boolean>(false)
  let [deleteEvent, setDeleteEvent] = useState<Event>()
  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let user = useAppSelector(selectUser)
  let roles = useAppSelector(selectRoles)
  let useEvents = useAppSelector(selectUseEvents)

  let includeFF = useAppSelector(selectFoamfratFilter)
  let eventTypeFilter = useAppSelector(selectEventTypeFilter)
  let dateLowerFilter = useAppSelector(selectDateLowerFilter)
  let dateUpperFilter = useAppSelector(selectDateUpperFilter)
  let accreditationFilter = useAppSelector(selectAccreditationFilter)

  let activeEvent = useAppSelector(selectActiveEvent)
  let events = useAppSelector(selectEvents)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let sessions = useAppSelector(selectSessions)
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let accreditations = useAppSelector(selectAccreditationsByType)
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditations = useAppSelector(selectEventAccreditations)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )
  let instructorsList = useAppSelector(selectInstructors)
  let instructorsById = useMemo(
    () => new Map(instructorsList.map((i) => [i.id, i])),
    [instructorsList],
  )
  let eventsById = useMemo(
    () => new Map(events.map((e) => [e.id, e])),
    [events],
  )
  let instructors = useMemo(
    () =>
      Object.fromEntries(
        sessions.map((s) => [
          s.id,
          (
            (s.instructorIDs.length
              ? s.instructorIDs
              : eventsById.get(s.eventID)?.instructorIDs) ?? []
          )
            .map((id) => instructorsById.get(id)?.name)
            .filter(Boolean)
            .join(', ') || '',
        ]),
      ),
    [sessions, instructorsById, eventsById],
  )
  let instructorsStatus = useAppSelector(selectInstructorsStatus)
  let invites = useAppSelector(selectInvites)
  let invitesStatus = useAppSelector(selectInvitesStatus)
  let usersSessionData = sessions.find((session) => session.id === usersSession)
  let lsid =
    events.find((ev) => ev.id === usersSessionData?.eventID)?.linkedServiceID ||
    ''
  let users = useAppSelector(selectUsersByLinkedService(lsid))
  let usersStatus = useAppSelector(selectUsersStatusByLinkedService(lsid))
  let usersList =
    invites[usersSession || '']
      ?.map((inv) => users?.find((u) => inv.userID === u.id))
      ?.filter((u) => !!u) || []

  let featuredEvents = events
    .filter((e) => e.featured)
    .filter((e) =>
      sessions.some(
        (s) =>
          s.eventID === e.id &&
          dayjs.utc(s.sessionTime).isAfter(dayjs().startOf('day')),
      ),
    )
    .filter(
      (e) =>
        (!nameFilter ||
          e.name.toLowerCase().includes(nameFilter.toLowerCase())) &&
        (eventTypeFilter === 'All' || e.eventType === eventTypeFilter) &&
        (accreditationFilter === 'All' ||
          accreditations[accreditationFilter].some((a) =>
            eventAccreditations.some(
              (ea) => ea.accreditationID === a.id && ea.eventID === e.id,
            ),
          )),
    )
    .slice(0, 3)
  let unscheduledEvents = events.filter(
    (e) =>
      (!!e.linkedServiceID || (includeFF && roles.includes('admin'))) &&
      !sessions.some((s) => s.eventID === e.id) &&
      (!nameFilter ||
        e.name.toLowerCase().includes(nameFilter.toLowerCase())) &&
      (eventTypeFilter === 'All' || e.eventType === eventTypeFilter) &&
      (accreditationFilter === 'All' ||
        accreditations[accreditationFilter].some((a) =>
          eventAccreditations.some((ea) => ea.accreditationID === a.id),
        )),
  )

  let sessionsByMonth = groupBy(
    (session: EventSession) =>
      dayjs.utc(session.sessionTime).tz(session.timeZone).format('MMMM YYYY'),
    sessions.filter((s) => {
      let sessionEvent = events.find((e) => e.id === s.eventID)
      return (
        (sessionEvent?.linkedServiceID ||
          (includeFF && !sessionEvent?.featured)) &&
        (!nameFilter ||
          sessionEvent?.name
            .toLowerCase()
            .includes(nameFilter.toLowerCase())) &&
        (eventTypeFilter === 'All' ||
          sessionEvent?.eventType === eventTypeFilter) &&
        dayjs
          .utc(s.sessionTime)
          .isBefore(dayjs(dateUpperFilter).endOf('day')) &&
        dayjs
          .utc(s.sessionTime)
          .isAfter(dayjs(dateLowerFilter).startOf('day')) &&
        (accreditationFilter === 'All' ||
          accreditations[accreditationFilter].some((a) =>
            eventAccreditations.some(
              (ea) =>
                ea.accreditationID === a.id && ea.eventID === sessionEvent?.id,
            ),
          ))
      )
    }),
  )

  let sortedMonths = Object.keys(sessionsByMonth).sort((a, b) =>
    dayjs
      .utc(sessionsByMonth[a]?.[0].sessionTime)
      .isAfter(dayjs.utc(sessionsByMonth[b]?.[0].sessionTime))
      ? 1
      : -1,
  )

  useAutoFetch(eventsStatus, fetchEvents)
  useAutoFetch(sessionsStatus, fetchSessions)
  useAutoFetch(accreditationsStatus, fetchAccreditations)
  useAutoFetch(eventAccreditationsStatus, fetchEventAccreditations)
  useAutoFetch(instructorsStatus, fetchInstructors)

  useEffect(() => {
    if (lsid && (!usersStatus || usersStatus === 'uninitialized')) {
      dispatch(fetchUsers(lsid))
    }
  }, [usersStatus, lsid])

  useEffect(() => {
    if (
      inviteSession &&
      (!invitesStatus[inviteSession] ||
        invitesStatus[inviteSession] === 'uninitialized')
    ) {
      dispatch(fetchInvites(inviteSession))
    } else if (inviteSession && invitesStatus[inviteSession] === 'fulfilled') {
      setCheckedUsers(invites[inviteSession]?.map((inv) => inv.userID))
    }
  }, [invitesStatus, inviteSession])

  useEffect(() => {
    if (
      usersSession &&
      (!invitesStatus[usersSession] ||
        invitesStatus[usersSession] === 'uninitialized')
    ) {
      dispatch(fetchInvites(usersSession))
    } else if (usersSession && invitesStatus[usersSession] === 'fulfilled') {
      setCheckedUsers(invites[usersSession]?.map((inv) => inv.userID))
    }
  }, [invitesStatus, usersSession])

  useEffect(() => {
    if (activeEvent) {
      dispatch(activeEventChanged('unselected'))
    }
  }, [activeEvent])

  let UsersToolbar = useCallback(
    () => <DataGridToolbar fileName={`registered-users-${usersSession}`} />,
    [usersSession],
  )

  let isLoaded = [
    eventsStatus,
    sessionsStatus,
    accreditationsStatus,
    eventAccreditationsStatus,
  ].every((s) => s === 'fulfilled' || s === 'rejected')

  let ffAsterisk = (
    <Typography component="span" color="secondary">
      *
    </Typography>
  )

  return useEvents ? (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          mb: 2,
          gap: 1,
        }}
      >
        <Typography variant="h4">Events</Typography>
        {roles.includes('manager') && (
          <Button
            size="large"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              dispatch(activeEventChanged('unselected'))
              navigate('/events/create')
            }}
          >
            Create Event
          </Button>
        )}
      </Box>
      {(featuredEvents.length > 0 || !isLoaded) && (
        <Typography sx={{ mb: 1 }} variant="h6">
          Featured Events
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 2,
          overflow: 'scroll',
        }}
      >
        {featuredEvents.map((event) => {
          let hours = Math.floor(event.hours) || 0
          let minutes = (event.hours - hours) * 60
          let duration = minutes ? `${hours}h ${minutes}m` : `${hours}h`

          let eventSessions = sessions
            .filter((s) => s.eventID === event.id)
            .sort((a, b) =>
              dayjs(a.sessionTime).isAfter(dayjs(b.sessionTime)) ? 1 : -1,
            )
          let nextSession = eventSessions[0]

          if (!nextSession) {
            return <></>
          }

          let nextSessionDate = dayjs
            .utc(nextSession.sessionTime)
            .tz(nextSession.timeZone)
            .format('LL')
          let nextSessionTime = dayjs
            .utc(nextSession.sessionTime)
            .tz(nextSession.timeZone)
            .format('h:mm A')

          return (
            <Card
              key={`event-${event.id}`}
              variant="outlined"
              onClick={() => navigate(`/events/${event?.id}`)}
              sx={{
                minWidth: 280,
                '&:hover': {
                  cursor: 'pointer',
                  bgcolor: 'background.paperDark',
                },
              }}
            >
              <CardMedia
                sx={{
                  objectFit: 'contain',
                  height: 180,
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: 1,
                }}
                image={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event.thumbnail?.replace('originals', 'resized')}`}
                title={event.name}
              >
                <Chip
                  // color="secondary"
                  sx={{ alignSelf: 'flex-end', fontSize: 14 }}
                  icon={<ClockIcon />}
                  label={duration}
                />
                <Chip
                  // color="primary"
                  sx={{ fontSize: 14 }}
                  label={event.eventType}
                />
              </CardMedia>
              <CardContent>
                <Typography gutterBottom sx={{ fontSize: 18 }}>
                  {event.name}
                </Typography>
                <Typography variant="subtitle2">
                  By {instructors[nextSession.id] || 'Instructor'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    mb: -1,
                    mt: 1,
                  }}
                >
                  <CalendarIcon sx={{ height: 16, width: 16 }} />
                  <Typography variant="subtitle2">
                    {nextSessionDate} {'\u2022'} {nextSessionTime}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )
        })}
        {!isLoaded && !featuredEvents.length && (
          <>
            <Skeleton variant="rounded" height={300} />
            <Skeleton variant="rounded" height={300} />
            <Skeleton variant="rounded" height={300} />
          </>
        )}
      </Box>
      <Card
        variant="outlined"
        sx={{ mt: 3, p: 2, pb: 1.5, display: 'inline-block' }}
      >
        <Typography variant="h6">Filter FOAMfrat Events</Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 0.5 }}>
          Toggle whether to see FOAMfrat events in your event schedule. When
          visible, FOAMfrat events are denoted by an asterisk ({ffAsterisk}).
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={includeFF}
              onChange={() => dispatch(foamfratFilterChanged(!includeFF))}
              slotProps={{ input: { 'aria-label': 'include-ff-events' } }}
            />
          }
          label={`${includeFF ? '' : 'No '}FOAMfrat Events Visible`}
        />
      </Card>
      <Box sx={{ mb: 2, mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <TextField
          label="Search"
          size="small"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl sx={{ minWidth: 156 }}>
          <Select
            value={eventTypeFilter}
            onChange={(e) =>
              dispatch(eventTypeFilterChanged(e.target.value as EventTypeName))
            }
            size="small"
          >
            <MenuItem value="All">All Event Types</MenuItem>
            <MenuItem value="Virtual">Virtual</MenuItem>
            <MenuItem value="Hybrid">Hybrid</MenuItem>
            <MenuItem value="In Person">In Person</MenuItem>
          </Select>
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ width: 160 }}
            label="From"
            value={dayjs(dateLowerFilter)}
            slotProps={{ textField: { size: 'small' } }}
            onChange={(newValue) =>
              dispatch(dateLowerFilterChanged(newValue as any))
            }
          />
          <DatePicker
            sx={{ width: 160 }}
            label="To"
            value={dayjs(dateUpperFilter)}
            slotProps={{ textField: { size: 'small' } }}
            onChange={(newValue) =>
              dispatch(dateUpperFilterChanged(newValue as any))
            }
          />
        </LocalizationProvider>
        <FormControl sx={{ minWidth: 156 }}>
          <Select
            value={accreditationFilter}
            onChange={(e) =>
              dispatch(accreditationFilterChanged(e.target.value as any))
            }
            size="small"
          >
            <MenuItem value="All">All Accreditations</MenuItem>
            {Object.entries(accreditationDict).map(([val, txt]) => (
              <MenuItem key={val} value={val}>
                {txt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <Box sx={{ flexGrow: 1 }}></Box>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, value) => setViewMode(value)}
        >
          <ToggleButton value="List" sx={{ px: 1, width: 115 }}>
            <BulletedListIcon sx={{ mr: 0.5, height: 18, width: 18 }} />
            <span>List View</span>
          </ToggleButton>
          <ToggleButton value="Calendar" sx={{ px: 1, width: 115 }} disabled>
            <CalendarIcon sx={{ mr: 0.5, height: 18, width: 18 }} />
            <span>Calendar</span>
          </ToggleButton>
        </ToggleButtonGroup> */}
      </Box>
      <Box>
        {sortedMonths.map((month) => (
          <Box
            key={month}
            sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            <Typography sx={{ mb: -0.5 }}>{month}</Typography>
            {sessionsByMonth[month]
              ?.sort((a, b) =>
                dayjs(a.sessionTime).isAfter(dayjs(b.sessionTime)) ? 1 : -1,
              )
              .map((session) => {
                let event = events.find((e) => e.id === session.eventID)
                let sessionName =
                  session.name ||
                  `Session ${
                    (sessions
                      .filter((s) => s.eventID === event?.id)
                      .sort((a, b) => dayjs(a.sessionTime).diff(b.sessionTime))
                      .findIndex((s) => s.id === session.id) || 0) + 1
                  }`
                // `invites` only holds the viewer's own invite for non-managers, so
                // count taken spots from the API's viewer-independent seatsTaken
                let seatsTaken = session.seatsTaken ?? session.invites.length
                let sessionDisabled =
                  !!session.capacity &&
                  seatsTaken >= session.capacity &&
                  !(
                    roles.includes('manager') ||
                    session.invites.some((inv) => inv.userID === user?.id)
                  )
                return (
                  <Paper
                    key={session.id}
                    onClick={() =>
                      sessionDisabled ? {} : navigate(`/events/${event?.id}`)
                    }
                    sx={{
                      p: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      alignItems: 'center',
                      bgcolor: sessionDisabled
                        ? 'background.paperDark'
                        : undefined,
                      '&:hover': sessionDisabled
                        ? {}
                        : {
                            cursor: 'pointer',
                            bgcolor: 'background.paperDark',
                          },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, mr: 1 }}>
                      <Box
                        className="flex items-center justify-center uppercase"
                        sx={{
                          width: 50,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 500,
                            lineHeight: 1,
                          }}
                          color="text.secondary"
                        >
                          {dayjs
                            .utc(session.sessionTime)
                            .tz(session.timeZone)
                            .format('ddd')}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            mb: -0.75,
                            mt: 0.5,
                          }}
                        >
                          {month.slice(0, 3)}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 24, fontWeight: 500 }}
                          color="primary"
                        >
                          {dayjs
                            .utc(session.sessionTime)
                            .tz(session.timeZone)
                            .format('DD')}
                        </Typography>
                      </Box>
                    </Box>
                    <Avatar
                      variant="rounded"
                      src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event?.thumbnail?.replace('originals', 'resized')}`}
                    />
                    <Box sx={{ flexGrow: 1, ml: 1 }}>
                      <Typography sx={{ mb: 0.5 }}>
                        {!event?.linkedServiceID && ffAsterisk}
                        {event?.name}{' '}
                        <Typography component="span" color="text.secondary">
                          {'\u2022'} {sessionName}
                        </Typography>
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          fontSize: 14,
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <ClockIcon
                            sx={{ height: 18, width: 18, mb: '2px', mr: '2px' }}
                          />
                          <Box component="span" color="text.secondary">
                            {dayjs
                              .utc(session.sessionTime)
                              .tz(session.timeZone)
                              .format('h:mm A (z)')}
                          </Box>
                        </Box>
                        <Box>
                          <LocationIcon
                            sx={{ height: 18, width: 18, mb: '2px', mr: '1px' }}
                          />
                          <Box component="span" color="text.secondary">
                            {event?.eventType}
                          </Box>
                        </Box>
                        <Box>
                          <UserIcon
                            sx={{ height: 18, width: 18, mb: '2px', mr: '1px' }}
                          />
                          <Box component="span" color="text.secondary">
                            {instructors[session.id]}
                          </Box>
                        </Box>
                        {session.capacity && (
                          <Box>
                            <GroupIcon
                              sx={{ height: 18, width: 18, mb: '2px', mr: 0.5 }}
                            />
                            <Box component="span" color="text.secondary">
                              {session.capacity > seatsTaken
                                ? `${session.capacity - seatsTaken} Spots Open`
                                : 'Session Full'}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {roles.includes(
                      !event?.linkedServiceID ? 'admin' : 'manager',
                    ) && (
                      <Box sx={{ mr: 1 }}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            setUsersSession(session.id)
                          }}
                        >
                          <GroupIcon />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            setInviteSession(session.id)
                          }}
                        >
                          <AddUserIcon />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/events/${event?.id}/edit`)
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteEvent(event)
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                )
              })}
          </Box>
        ))}
      </Box>
      {roles.includes('manager') && unscheduledEvents.length > 0 && (
        <Box sx={{ my: 2 }}>
          <Typography sx={{ mb: 0.5 }}>Unscheduled Events</Typography>
          <Box sx={{ maxHeight: 400, overflow: 'scroll' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {unscheduledEvents.map((event) => (
                <Card
                  key={event.id}
                  sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'center' }}
                >
                  <Avatar
                    variant="rounded"
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event?.thumbnail?.replace('originals', 'resized')}`}
                  />
                  <Typography sx={{ flexGrow: 1 }}>
                    {!event?.linkedServiceID && ffAsterisk}
                    {event.name}
                  </Typography>
                  <Box sx={{ mr: 0.5 }}>
                    <IconButton
                      onClick={() => navigate(`/events/${event?.id}/edit`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => setDeleteEvent(event)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>
      )}
      {isLoaded &&
        sortedMonths.length === 0 &&
        unscheduledEvents.length === 0 && (
          <Typography>There are no events matching these filters.</Typography>
        )}
      {!isLoaded && (
        <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3].map((x) => (
            <React.Fragment key={x}>
              <Typography variant="h5">
                <Skeleton />
              </Typography>
              <Skeleton variant="rounded" height={68} />
              <Skeleton variant="rounded" height={68} />
            </React.Fragment>
          ))}
        </Box>
      )}
      <Dialog onClose={() => setDeleteEvent(undefined)} open={!!deleteEvent}>
        <Paper className="px-3 py-1" sx={{ maxWidth: 300 }}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            Confirm Event Deletion
          </DialogTitle>
          <DialogContent className="px-2">
            Are you sure you want to delete {deleteEvent?.name}?
          </DialogContent>
          <DialogActions className="flex justify-center pt-0 pb-3">
            <Button
              onClick={() => {
                setLoading(true)
                axios
                  .delete(`/api/event/${deleteEvent?.id}`)
                  .then(() => dispatch(fetchEvents()))
                  .then(() => dispatch(fetchSessions()))
                  .catch((err) => setErrorMessage(pluckError(err)))
                  .finally(() => {
                    setDeleteEvent(undefined)
                    setLoading(false)
                  })
              }}
              color="primary"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              loading={loading}
              startIcon={<ConfirmIcon />}
            >
              Confirm
            </Button>
            <Button
              onClick={() => setDeleteEvent(undefined)}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
      <Drawer
        anchor="right"
        onClose={() => setUsersSession(undefined)}
        open={!!usersSession}
      >
        <Box
          sx={{
            mt: '80px',
            px: 2,
            pb: 1.5,
            width: { xs: '100vw', sm: 700 },
            bgcolor: 'black',
            height: 'calc(100% - 80px)',
            display: 'flex',
            flexDirection: 'column',
            borderLeftWidth: '1px',
            borderLeftStyle: 'solid',
            borderLeftColor: 'divider',
          }}
        >
          <Typography
            color="textSecondary"
            sx={{
              textAlign: 'right',
              mt: 1,
            }}
          >
            {
              events.find(
                (ev) =>
                  sessions.find((s) => s.id === usersSession)?.eventID ===
                  ev.id,
              )?.name
            }
          </Typography>
          <Typography align="center" variant="h6" sx={{ mt: 1.5, mb: 1 }}>
            Registered Users
          </Typography>
          {invitesStatus[usersSession || ''] === 'fulfilled' ? (
            <DataGridPremium
              rows={usersList.map((u) => ({
                id: u.id,
                firstName: u.firstName || '',
                lastName: u.lastName || '',
                primaryEmail: u.primaryEmail || '',
              }))}
              columns={[
                {
                  field: 'lastName',
                  headerName: 'Last Name',
                  flex: 1,
                  minWidth: 130,
                },
                {
                  field: 'firstName',
                  headerName: 'First Name',
                  flex: 1,
                  minWidth: 130,
                },
                {
                  field: 'primaryEmail',
                  headerName: 'Email',
                  flex: 2,
                  minWidth: 200,
                },
              ]}
              slots={{ toolbar: UsersToolbar }}
              showToolbar
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: 'lastName', sort: 'asc' }] },
              }}
              disableRowSelectionOnClick
              sx={{ ...dataGridSx, flexGrow: 1 }}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Skeleton variant="rounded" height={54} />
              <Skeleton variant="rounded" height={54} />
              <Skeleton variant="rounded" height={54} />
              <Skeleton variant="rounded" height={54} />
            </Box>
          )}
          <Box sx={{ mt: 1.5 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setUsersSession(undefined)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>
      <Drawer
        anchor="right"
        onClose={() => setInviteSession(undefined)}
        open={!!inviteSession}
      >
        <Box
          sx={{
            mt: '80px',
            px: 2,
            pb: 1.5,
            minWidth: { xs: 320, sm: 600 },
            bgcolor: 'black',
            // height: 'calc(100% - 80px)',
            height: '100vh',
            borderLeftWidth: '1px',
            borderLeftStyle: 'solid',
            borderLeftColor: 'divider',
          }}
        >
          <Typography
            color="textSecondary"
            sx={{
              textAlign: 'right',
              mt: 1,
            }}
          >
            {
              events.find(
                (ev) =>
                  sessions.find((s) => s.id === inviteSession)?.eventID ===
                  ev.id,
              )?.name
            }
          </Typography>
          {invitesStatus[inviteSession || ''] === 'fulfilled' ? (
            <InviteUsers
              sessionID={inviteSession!}
              checkedUsers={checkedUsers}
              setCheckedUsers={setCheckedUsers}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Skeleton variant="rounded" height={54} />
              <Skeleton variant="rounded" height={54} />
              <Skeleton variant="rounded" height={54} />
              <Skeleton variant="rounded" height={54} />
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Button
              variant="contained"
              loading={inviting}
              disabled={invitesStatus[inviteSession || ''] !== 'fulfilled'}
              onClick={() => {
                setInviting(true)
                axios
                  .put('/api/event/session-invites', {
                    id: inviteSession,
                    userIDs: checkedUsers,
                  })
                  .then(() => dispatch(fetchInvites(inviteSession || '')))
                  .then(() => setInviteSession(undefined))
                  .catch((err) => setErrorMessage(pluckError(err)))
                  .finally(() => setInviting(false))
              }}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setInviteSession(undefined)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  ) : (
    <></>
  )
}
